import { useEffect, useState, useRef, useMemo } from 'react';
import { Send, Search, User, Users, UserPlus, FileText, ChevronDown, Paperclip, Trash2, Reply, ReplyAll, Forward, Bold, Italic, Underline, List, AlignLeft, AlignCenter, AlignRight, Clock, AlertTriangle, Save, FolderOpen } from 'lucide-react';
import { api } from '../lib/api';
import { improveAiErrorUserMessage } from '../lib/improveAiError';
import toast from 'react-hot-toast';
import EmailCredentialModal from './EmailCredentialModal';
import { getPhotoUrl } from '../lib/photoUtils';
import { useQuery } from 'react-query';
import { Badge, Button, Chip, Modal } from './ui';

type ImapDraftListItem = {
  uid: number;
  subject: string;
  date: Date;
  to?: string;
  cc?: string;
};

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  to: string | undefined;
  studentName?: string;
  initialSubject?: string;
  initialBody?: string;
  studentUnid?: string;
  studentUnids?: string[];
  context?: 'lernbuero' | 'sitzordnung' | 'email-client';
  replyMode?: 'reply' | 'replyAll' | 'forward';
  originalEmail?: {
    from: string;
    to: string;
    cc?: string;
    subject: string;
    body: string;
    html?: string;
    date: Date | string;
    messageId?: string;
    inReplyTo?: string;
  };
  initialDraft?: {
    uid: number;
    folder: string;
    subject: string;
    to?: string;
    cc?: string;
  };
}

export default function EmailModal({ isOpen, onClose, to, studentName, initialSubject, initialBody, studentUnid, studentUnids, context = 'lernbuero', replyMode, originalEmail, initialDraft }: EmailModalProps) {
  const effectiveStudentUnids = studentUnids?.length ? studentUnids : studentUnid ? [studentUnid] : [];
  const isSeparateStudentBatch = effectiveStudentUnids.length > 1;
  const activeStudentUnid = !isSeparateStudentBatch ? effectiveStudentUnids[0] : undefined;
  // All hooks must be called unconditionally and in the same order
  const [subject, setSubject] = useState(initialSubject || '');
  const [body, setBody] = useState(initialBody || '');
  const [toAddress, setToAddress] = useState(to || '');
  const [sendAsHtml, setSendAsHtml] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bccSender, setBccSender] = useState(true);
  const [fromEmail, setFromEmail] = useState<string | undefined>(undefined);
  const [hasSentMailbox, setHasSentMailbox] = useState<boolean>(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [parentEmails, setParentEmails] = useState<string[]>([]);
  const [parentEmailsLoading, setParentEmailsLoading] = useState(false);
  const [translateToFamilyLanguage, setTranslateToFamilyLanguage] = useState(false);
  const [familyLanguage, setFamilyLanguage] = useState<string | null>(null);
  const [translationLoading, setTranslationLoading] = useState(false);
  const [ccClassTeacher, setCcClassTeacher] = useState(true);
  const [classTeacherEmail, setClassTeacherEmail] = useState<string | null>(null);
  const [ccSchoolLeadership, setCcSchoolLeadership] = useState(false);
  const [showEmailCredentialModal, setShowEmailCredentialModal] = useState(false);
  const [scheduleLater, setScheduleLater] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  
  // In private Akte ablegen (manuell oder automatisch nach Empfänger)
  const [saveToAkte, setSaveToAkte] = useState(false);
  const [saveToAkteMode, setSaveToAkteMode] = useState<'manual' | 'auto'>('auto');
  const [saveToAkteStudentUnids, setSaveToAkteStudentUnids] = useState<string[]>([]);
  const [autoResolvedByEmail, setAutoResolvedByEmail] = useState<Array<{ email: string; students: Array<{ sophomorix_unid: string; student_name: string; klasse: string }> }>>([]);
  const [saveToAkteResolveLoading, setSaveToAkteResolveLoading] = useState(false);
  const [saveToAkteStudentInfo, setSaveToAkteStudentInfo] = useState<Record<string, { student_name: string; klasse: string }>>({});
  const [akteStudentSearch, setAkteStudentSearch] = useState('');
  const [akteDisambiguationStatus, setAkteDisambiguationStatus] = useState<'idle' | 'resolving' | 'llm_analyzing' | 'unambiguous' | 'ambiguous'>('idle');
  const [akteDisambiguationReasoning, setAkteDisambiguationReasoning] = useState<string | null>(null);
  
  // Address book integration
  const [addressBookSearch, setAddressBookSearch] = useState('');
  const [addressBookResults, setAddressBookResults] = useState<any[]>([]);
  const [addressBookLoading, setAddressBookLoading] = useState(false);
  const [showAddressBookResults, setShowAddressBookResults] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<Array<{email: string, name: string, type: 'to' | 'cc' | 'bcc'}>>([]);
  const [activeRecipientField, setActiveRecipientField] = useState<'to' | 'cc' | 'bcc' | null>(null);
  const [removedReplyDisplayEmails, setRemovedReplyDisplayEmails] = useState<Set<string>>(new Set());
  const addressBookRef = useRef<HTMLDivElement>(null);

  // Email templates
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);
  const [showDraftsMenu, setShowDraftsMenu] = useState(false);
  const draftsMenuRef = useRef<HTMLDivElement>(null);
  const [draftsMailbox, setDraftsMailbox] = useState<string | null>(null);
  const [imapDraftUid, setImapDraftUid] = useState<number | null>(null);
  const [imapDrafts, setImapDrafts] = useState<ImapDraftListItem[]>([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  
  // Attachments
  const [attachments, setAttachments] = useState<Array<{file: File, name: string, size: number}>>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const htmlEditorRef = useRef<HTMLDivElement>(null);
  const initialDraftAppliedRef = useRef<number | null>(null);

  const addAttachmentFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    setAttachments((prev) => [
      ...prev,
      ...files.map((file) => ({ file, name: file.name, size: file.size })),
    ]);
  };

  const handleComposeDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleComposeDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (Array.from(e.dataTransfer.types).includes('Files')) {
      dragDepthRef.current += 1;
      setIsDragging(true);
    }
  };

  const handleComposeDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragging(false);
    }
  };

  const handleComposeDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      addAttachmentFiles(e.dataTransfer.files);
    }
  };

  // Content warning modal (LLM-Inhaltsanalyse)
  const [showContentWarningModal, setShowContentWarningModal] = useState(false);
  const [contentWarningReason, setContentWarningReason] = useState<string | null>(null);
  const [contentWarningSuggestions, setContentWarningSuggestions] = useState<string[]>([]);
  const [contentWarningRecipientEmails, setContentWarningRecipientEmails] = useState<string[]>([]);
  const [excludeFromFutureCheck, setExcludeFromFutureCheck] = useState(false);
  const [contentWarningAnalyzing, setContentWarningAnalyzing] = useState(false);
  const pendingSendRef = useRef<(() => Promise<void>) | null>(null);
  
  // Fetch email templates - ALWAYS call useQuery unconditionally (hook order must be consistent)
  // Use enabled flag to control when query executes, but hook is always called
  const { data: templatesData } = useQuery(
    ['email-templates'],
    async () => {
      const response = await api.get('/email-client-advanced/templates');
      return response.data?.data || [];
    },
    {
      enabled: isOpen, // Only fetch when modal is open, but hook always runs
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      refetchOnMount: false, // Don't refetch on mount if data exists
      refetchOnWindowFocus: false, // Don't refetch on window focus
      onError: (error: any) => {
        console.error('Error fetching templates:', error);
      }
    }
  );

  // Always initialize templates array (never undefined)
  const templates = Array.isArray(templatesData) ? templatesData : [];

  const { data: akteStudentsData } = useQuery(
    ['students', 'akte-search', akteStudentSearch],
    async () => {
      const response = await api.get('/students', { params: { search: akteStudentSearch, sortField: 'Name', sortDirection: 'asc' } });
      return response.data?.data || [];
    },
    { enabled: isOpen && saveToAkte && saveToAkteMode === 'manual' && akteStudentSearch.length >= 2, staleTime: 60 * 1000 }
  );
  const akteSearchStudents = Array.isArray(akteStudentsData) ? akteStudentsData : [];

  const { data: scheduledEmailTimezoneData } = useQuery(
    ['scheduled-email-timezone'],
    async () => {
      const response = await api.get('/settings/scheduled-email-timezone');
      return response.data?.data?.timezone ?? null;
    },
    { enabled: isOpen, staleTime: 5 * 60 * 1000 }
  );
  const scheduledEmailTimezone = scheduledEmailTimezoneData ?? null;

  const { data: emailContentCheckData, refetch: refetchEmailContentCheck } = useQuery(
    ['settings', 'email-content-check'],
    async () => {
      const response = await api.get('/settings/email-content-check');
      const d = response.data?.data;
      return {
        enabled: d?.enabled !== false,
        excludedRecipients: Array.isArray(d?.excludedRecipients) ? d.excludedRecipients : []
      };
    },
    { enabled: isOpen, staleTime: 2 * 60 * 1000 }
  );
  const emailContentCheckEnabled = emailContentCheckData?.enabled !== false;
  const contentCheckExcludedRecipients: string[] = emailContentCheckData?.excludedRecipients ?? [];

  const getNextMorningValue = () => {
    const now = new Date();
    const next = new Date(now);
    next.setDate(now.getHours() >= 8 ? now.getDate() + 1 : now.getDate());
    next.setHours(8, 0, 0, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}T${pad(next.getHours())}:${pad(next.getMinutes())}`;
  };

  const fetchNextMorningAndSet = async () => {
    if (scheduledEmailTimezone) {
      try {
        const res = await api.get('/email/next-morning-local', { params: { timezone: scheduledEmailTimezone } });
        if (res.data?.success && res.data?.data?.local) {
          setScheduledAt(res.data.data.local);
        } else {
          setScheduledAt(getNextMorningValue());
        }
      } catch {
        setScheduledAt(getNextMorningValue());
      }
    } else {
      setScheduledAt(getNextMorningValue());
    }
  };

  // Parse "Name <email@x.de>" or "email@x.de" into { email, name } (used for reply chips and effect)
  const parseEmailAddresses = (emailString: string): Array<{email: string, name: string}> => {
    if (!emailString) return [];
    return emailString.split(',').map(addr => {
      const trimmed = addr.trim();
      const match = trimmed.match(/^(.+?)\s*<(.+?)>$|^(.+?)$/);
      if (match) {
        return { email: match[2] || match[3] || trimmed, name: match[1] || '' };
      }
      return { email: trimmed, name: '' };
    }).filter(item => item.email.includes('@'));
  };

  // Reply/ReplyAll: Empfänger aus originalEmail für die Anzeige (Chips), damit sie sofort sichtbar sind
  const replyToRecipientsDisplay = useMemo((): Array<{email: string, name: string, type: 'to' | 'cc' | 'bcc'}> => {
    if ((replyMode !== 'reply' && replyMode !== 'replyAll') || !originalEmail) return [];
    const fromEmails = parseEmailAddresses(originalEmail.from);
    if (fromEmails.length === 0) return [{ email: originalEmail.from, name: originalEmail.from, type: 'to' }];
    return [{ email: fromEmails[0].email, name: fromEmails[0].name || fromEmails[0].email, type: 'to' }];
  }, [replyMode, originalEmail]);

  const replyCcRecipientsDisplay = useMemo((): Array<{email: string, name: string, type: 'to' | 'cc' | 'bcc'}> => {
    if (replyMode !== 'replyAll' || !originalEmail) return [];
    const fromEmails = parseEmailAddresses(originalEmail.from);
    const sender = fromEmails.length > 0 ? fromEmails[0].email.toLowerCase() : originalEmail.from.toLowerCase();
    const toEmails = parseEmailAddresses(originalEmail.to || '');
    const ccEmails = parseEmailAddresses(originalEmail.cc || '');
    return [...toEmails, ...ccEmails]
      .filter(e => e.email.toLowerCase() !== sender)
      .map(e => ({ email: e.email, name: e.name || e.email, type: 'cc' as const }));
  }, [replyMode, originalEmail]);

  // ALL useEffect hooks must be called unconditionally - they can have early returns inside
  useEffect(() => {
    if (!isOpen) return;
    
    // Handle Reply/Reply All/Forward modes - only if replyMode and originalEmail are set
    if (replyMode && originalEmail) {
      setRemovedReplyDisplayEmails(new Set());
      if (replyMode === 'reply') {
        // Reply: Set "To" to original sender, subject with "Re:"
        const replySubject = originalEmail.subject.startsWith('Re:') 
          ? originalEmail.subject 
          : `Re: ${originalEmail.subject}`;
        setSubject(replySubject);
        
        // Parse the original sender's email address
        const fromEmails = parseEmailAddresses(originalEmail.from);
        if (fromEmails.length > 0) {
          // Add the sender as a recipient in the "To" field
          setSelectedRecipients([{
            email: fromEmails[0].email,
            name: fromEmails[0].name || fromEmails[0].email,
            type: 'to'
          }]);
          setToAddress(fromEmails[0].email);
        } else {
          // Fallback: use the raw from string if parsing fails
          setSelectedRecipients([{
            email: originalEmail.from,
            name: originalEmail.from,
            type: 'to'
          }]);
          setToAddress(originalEmail.from);
        }
        // Clear the search field
        setAddressBookSearch('');
        
        // Quote original email in body
        const originalDate = new Date(originalEmail.date).toLocaleString('de-DE');
        const emailBody = originalEmail.html || originalEmail.body || '';
        const plainBody = emailBody.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
        const quotedBody = `\n\n--- Originalnachricht ---\nVon: ${originalEmail.from}\nAn: ${originalEmail.to}\nDatum: ${originalDate}\nBetreff: ${originalEmail.subject}\n\n${plainBody}`;
        setBody(quotedBody);
      } else if (replyMode === 'replyAll') {
        // Reply All: Set original sender as "To", add all other recipients to CC
        const replySubject = originalEmail.subject.startsWith('Re:') 
          ? originalEmail.subject 
          : `Re: ${originalEmail.subject}`;
        setSubject(replySubject);
        
        // Parse the original sender's email address
        const fromEmails = parseEmailAddresses(originalEmail.from);
        const senderRecipient = fromEmails.length > 0 
          ? { email: fromEmails[0].email, name: fromEmails[0].name || fromEmails[0].email, type: 'to' as const }
          : { email: originalEmail.from, name: originalEmail.from, type: 'to' as const };
        
        setToAddress(senderRecipient.email);
        
        // Parse all other recipients (original To and CC, excluding the sender)
        const originalToEmails = parseEmailAddresses(originalEmail.to || '');
        const originalCcEmails = parseEmailAddresses(originalEmail.cc || '');
        
        // Get sender email for comparison
        const senderEmail = senderRecipient.email.toLowerCase();
        
        // Combine all recipients (To + CC) and exclude the sender
        const ccRecipients = [...originalToEmails, ...originalCcEmails]
          .filter(e => e.email.toLowerCase() !== senderEmail)
          .map(e => ({ email: e.email, name: e.name || e.email, type: 'cc' as const }));
        
        // Add sender as "To" and all other recipients as "CC"
        setSelectedRecipients([senderRecipient, ...ccRecipients]);
        // Clear the search field
        setAddressBookSearch('');
        
        // Quote original email in body
        const originalDate = new Date(originalEmail.date).toLocaleString('de-DE');
        const emailBody = originalEmail.html || originalEmail.body || '';
        const plainBody = emailBody.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
        const quotedBody = `\n\n--- Originalnachricht ---\nVon: ${originalEmail.from}\nAn: ${originalEmail.to}\nCC: ${originalEmail.cc || ''}\nDatum: ${originalDate}\nBetreff: ${originalEmail.subject}\n\n${plainBody}`;
        setBody(quotedBody);
      } else if (replyMode === 'forward') {
        // Forward: Keep original subject with "Fwd:", empty To field
        const forwardSubject = originalEmail.subject.startsWith('Fwd:') || originalEmail.subject.startsWith('Fw:')
          ? originalEmail.subject
          : `Fwd: ${originalEmail.subject}`;
        setSubject(forwardSubject);
        setToAddress('');
        setSelectedRecipients([]);
        
        // Quote original email in body
        const originalDate = new Date(originalEmail.date).toLocaleString('de-DE');
        const emailBody = originalEmail.html || originalEmail.body || '';
        const plainBody = emailBody.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
        const quotedBody = `\n\n--- Weitergeleitete Nachricht ---\nVon: ${originalEmail.from}\nAn: ${originalEmail.to}\nCC: ${originalEmail.cc || ''}\nDatum: ${originalDate}\nBetreff: ${originalEmail.subject}\n\n${plainBody}`;
        setBody(quotedBody);
      }
      // Don't reset scheduleLater/scheduledAt for reply modes
      return;
    }
    
    // Normal compose mode - only reset if NOT in reply mode or loading an existing draft
    if (!replyMode && !initialDraft) {
      setSubject(initialSubject || '');
      setBody(initialBody || '');
      setToAddress(to || '');
      setSelectedRecipients([]);
      setRemovedReplyDisplayEmails(new Set());
      setScheduleLater(false);
      setScheduledAt('');
      setSaveToAkte(false);
      setSaveToAkteStudentUnids([]);
      setSaveToAkteStudentInfo({});
      setAutoResolvedByEmail([]);
      setAkteDisambiguationStatus('idle');
      setAkteDisambiguationReasoning(null);
      setImapDraftUid(null);
    }
  }, [isOpen, initialSubject, initialBody, to, replyMode, originalEmail, initialDraft]);

  useEffect(() => {
    if (!isOpen) {
      initialDraftAppliedRef.current = null;
      return;
    }
    if (!initialDraft || replyMode) return;
    if (initialDraftAppliedRef.current === initialDraft.uid) return;
    initialDraftAppliedRef.current = initialDraft.uid;

    const loadInitialDraft = async () => {
      const box = initialDraft.folder || draftsMailbox || 'Drafts';
      try {
        const res = await api.get(`/email-client/email/${initialDraft.uid}/body`, {
          params: { box, ...(fromEmail ? { emailAddress: fromEmail } : {}) }
        });
        const data = res.data?.data;
        const htmlBody = data?.body_html || '';
        const textBody = data?.body_text || '';
        const useHtml = !!htmlBody;
        const bodyContent = useHtml ? htmlBody : textBody;
        const recipients: Array<{ email: string; name: string; type: 'to' | 'cc' | 'bcc' }> = [];
        if (initialDraft.to) {
          for (const p of parseEmailAddresses(initialDraft.to)) {
            recipients.push({ email: p.email, name: p.name || p.email, type: 'to' });
          }
        }
        if (initialDraft.cc) {
          for (const p of parseEmailAddresses(initialDraft.cc)) {
            recipients.push({ email: p.email, name: p.name || p.email, type: 'cc' });
          }
        }

        setSubject(initialDraft.subject === '(Kein Betreff)' ? '' : initialDraft.subject);
        setBody(bodyContent);
        setSendAsHtml(useHtml);
        setSelectedRecipients(recipients);
        setImapDraftUid(initialDraft.uid);
        setDraftsMailbox(box);
        if (useHtml) {
          setTimeout(() => {
            if (htmlEditorRef.current) {
              const html =
                bodyContent && bodyContent.includes('<') && bodyContent.includes('>')
                  ? bodyContent
                  : (bodyContent || '')
                      .split('\n')
                      .map((l: string) => `<p>${(l || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`)
                      .join('');
              htmlEditorRef.current.innerHTML = html || '';
            }
          }, 0);
        }
      } catch (err: unknown) {
        const e = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
        toast.error(e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Entwurf konnte nicht geladen werden');
      }
    };

    loadInitialDraft();
  }, [isOpen, initialDraft, replyMode, draftsMailbox, fromEmail]);


  // Lade Eltern-E-Mail-Adressen, Familiensprache und Klassenlehrer-E-Mail wenn Modal geöffnet wird
  useEffect(() => {
    if (!isOpen || !activeStudentUnid || isSeparateStudentBatch) {
      if (!isOpen || isSeparateStudentBatch) {
        setParentEmails([]);
        setFamilyLanguage(null);
        setTranslateToFamilyLanguage(false);
        setClassTeacherEmail(null);
      }
      return;
    }

    const loadStudentData = async () => {
      setParentEmailsLoading(true);
      try {
        // Lade Eltern-E-Mail-Adressen
        const res = await api.get(`/email/parents/${activeStudentUnid}`);
        const parentEmailsData = res.data?.data?.parentEmails || [];
        setParentEmails(parentEmailsData);
        
        // Zeige Fehlermeldung wenn keine E-Mail-Adressen gefunden werden
        if (parentEmailsData.length === 0) {
          toast.error('Keine Eltern-E-Mail-Adressen für diesen Schüler hinterlegt');
        }

        // Lade Familiensprache
        const familyLangRes = await api.get(`/email/family-language/${activeStudentUnid}`);
        const familyLangData = familyLangRes.data?.data;
        if (familyLangData?.language) {
          setFamilyLanguage(familyLangData.language);
          setTranslateToFamilyLanguage(true); // Automatisch aktivieren wenn Sprache vorhanden
        } else {
          setFamilyLanguage(null);
          setTranslateToFamilyLanguage(false);
        }

        // Lade Klassenlehrer-E-Mail-Adresse
        const classTeacherRes = await api.get(`/email/class-teacher/${activeStudentUnid}`);
        const classTeacherData = classTeacherRes.data?.data;
        if (classTeacherData?.email) {
          setClassTeacherEmail(classTeacherData.email);
        } else {
          setClassTeacherEmail(null);
        }
      } catch (error) {
        console.error('Error loading student data:', error);
        setParentEmails([]);
        setFamilyLanguage(null);
        setTranslateToFamilyLanguage(false);
        setClassTeacherEmail(null);
        toast.error('Schülerdaten konnten nicht geladen werden');
      } finally {
        setParentEmailsLoading(false);
      }
    };

    loadStudentData();
  }, [isOpen, activeStudentUnid, isSeparateStudentBatch]);

  // Load user's default email credentials and whether Sent mailbox exists (BCC only if no Sent)
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const res = await api.get('/email/credentials');
        const creds = res.data?.data || [];
        const primary = creds.find((c: any) => c.is_default) || creds[0];
        setFromEmail(primary?.email_address);
        setHasSentMailbox(!!(primary?.detected_sent_mailbox));
      } catch (e) {
        setHasSentMailbox(false);
      }
    })();
  }, [isOpen]);

  // Search address book (mixed: personal, global, teachers, students, parents)
  useEffect(() => {
    if (!isOpen || !addressBookSearch.trim() || addressBookSearch.length < 2) {
      setAddressBookResults([]);
      setShowAddressBookResults(false);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setAddressBookLoading(true);
      try {
        // Search all types: personal, global, teacher, student
        const response = await api.get('/address-book', {
          params: { search: addressBookSearch, type: 'all', limit: 30 }
        });
        if (response.data?.success) {
          // Also search for parent emails if search term looks like an email or name
          let allResults = response.data.data || [];

          try {
            const staffRes = await api.get('/address-book/staff-recipient-suggest', {
              params: { search: addressBookSearch.trim(), limit: 20 }
            });
            if (staffRes.data?.success && Array.isArray(staffRes.data.data)) {
              const existingEmails = new Set(allResults.map((r: any) => String(r.email || '').toLowerCase()));
              for (const r of staffRes.data.data) {
                const em = String(r.email || '').toLowerCase();
                if (em && !existingEmails.has(em)) {
                  existingEmails.add(em);
                  allResults.push(r);
                }
              }
            }
          } catch {
            // optional endpoint
          }
          
          // If search looks like an email, also try to find parent emails
          if (isValidEmail(addressBookSearch.trim())) {
            try {
              const parentResponse = await api.get('/address-book/match-email', {
                params: { emails: addressBookSearch.trim() }
              });
              if (parentResponse.data?.success && parentResponse.data.data) {
                // Merge parent results, avoiding duplicates
                const existingEmails = new Set(allResults.map((r: any) => r.email?.toLowerCase()));
                const newParentResults = parentResponse.data.data.filter((r: any) => 
                  r.email && !existingEmails.has(r.email.toLowerCase())
                );
                allResults = [...allResults, ...newParentResults];
              }
            } catch (error) {
              // Ignore errors in parent search
            }
          }
          
          setAddressBookResults(allResults);
          setShowAddressBookResults(true);
        }
      } catch (error) {
        console.error('Error searching address book:', error);
      } finally {
        setAddressBookLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [addressBookSearch, isOpen]);

  // Close address book results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressBookRef.current && !addressBookRef.current.contains(event.target as Node)) {
        setShowAddressBookResults(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      dragDepthRef.current = 0;
      setIsDragging(false);
    }
  }, [isOpen]);

  // Initialize selected recipients from to prop
  useEffect(() => {
    if (isOpen && to && !studentUnid) {
      setSelectedRecipients([{ email: to, name: to, type: 'to' }]);
      setToAddress(to);
    } else if (isOpen && !to && !studentUnid) {
      setSelectedRecipients([]);
    }
  }, [isOpen, to, studentUnid]);

  // Close template dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (templateRef.current && !templateRef.current.contains(event.target as Node)) {
        setShowTemplateDropdown(false);
      }
    };

    if (isOpen && showTemplateDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, showTemplateDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (draftsMenuRef.current && !draftsMenuRef.current.contains(event.target as Node)) {
        setShowDraftsMenu(false);
      }
    };

    if (isOpen && showDraftsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, showDraftsMenu]);

  useEffect(() => {
    if (!isOpen) return;
    api.get('/email-client/mailbox-config', { params: fromEmail ? { emailAddress: fromEmail } : {} })
      .then((res) => {
        const drafts = res.data?.data?.drafts;
        setDraftsMailbox(typeof drafts === 'string' && drafts ? drafts : 'Drafts');
      })
      .catch(() => setDraftsMailbox('Drafts'));
  }, [isOpen, fromEmail]);

  // Sync body -> HTML editor when opening or switching to HTML (must be before early return)
  useEffect(() => {
    if (!isOpen || !sendAsHtml || !htmlEditorRef.current) return;
    const html = (body && body.includes('<') && body.includes('>')) ? body : (body || '').split('\n').map(l => `<p>${(l || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`).join('');
    htmlEditorRef.current.innerHTML = html || '';
  }, [isOpen, sendAsHtml]);

  // Auto-resolve recipient emails to students, then LLM disambiguation when multiple candidates
  useEffect(() => {
    if (!isOpen || !saveToAkte || saveToAkteMode !== 'auto') {
      setAutoResolvedByEmail([]);
      setAkteDisambiguationStatus('idle');
      return;
    }
    const toList = selectedRecipients.filter(r => r.type === 'to').length > 0
      ? selectedRecipients.filter(r => r.type === 'to')
      : replyToRecipientsDisplay.filter(r => !removedReplyDisplayEmails.has(r.email.toLowerCase()));
    const ccList = selectedRecipients.filter(r => r.type === 'cc').length > 0
      ? selectedRecipients.filter(r => r.type === 'cc')
      : replyCcRecipientsDisplay.filter(r => !removedReplyDisplayEmails.has(r.email.toLowerCase()));
    const emails = [...toList, ...ccList].map(r => r.email).filter(e => e && e.includes('@'));
    const unique = [...new Set(emails)];
    if (unique.length === 0) {
      setAutoResolvedByEmail([]);
      setSaveToAkteStudentUnids([]);
      setAkteDisambiguationStatus('idle');
      return;
    }
    setSaveToAkteResolveLoading(true);
    setAkteDisambiguationStatus('resolving');
    api.get('/email-client/students-by-recipient-emails', { params: { emails: unique.join(',') } })
      .then((res: any) => {
        if (!res.data?.success || !Array.isArray(res.data.data)) {
          setAutoResolvedByEmail([]);
          setSaveToAkteStudentUnids([]);
          setAkteDisambiguationStatus('idle');
          return;
        }
        setAutoResolvedByEmail(res.data.data);
        const allUnids = new Set<string>();
        const info: Record<string, { student_name: string; klasse: string }> = {};
        res.data.data.forEach((item: any) => {
          (item.students || []).forEach((s: any) => {
            allUnids.add(s.sophomorix_unid);
            info[s.sophomorix_unid] = { student_name: s.student_name || '', klasse: s.klasse || '' };
          });
        });
        const unidList = Array.from(allUnids);
        setSaveToAkteStudentUnids(unidList);
        setSaveToAkteStudentInfo(info);

        if (unidList.length === 0) {
          setSaveToAkteResolveLoading(false);
          setAkteDisambiguationStatus('idle');
          return;
        }
        if (unidList.length === 1) {
          setSaveToAkteResolveLoading(false);
          setAkteDisambiguationStatus('unambiguous');
          return;
        }

        setAkteDisambiguationStatus('llm_analyzing');
        const candidateStudents = unidList.map((unid: string) => ({
          sophomorix_unid: unid,
          student_name: info[unid]?.student_name ?? '',
          klasse: info[unid]?.klasse ?? ''
        }));
        const plainBody = (body || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
        api.post('/email-client-advanced/composed-email/disambiguate-students', {
          subject: subject || '',
          body: plainBody || body || '',
          candidateStudents
        })
          .then((llmRes: any) => {
            if (llmRes.data?.success && llmRes.data.data) {
              const d = llmRes.data.data;
              setAkteDisambiguationReasoning(d.reasoning || null);
              if (d.unambiguous && Array.isArray(d.suggested_unids) && d.suggested_unids.length > 0) {
                setSaveToAkteStudentUnids(d.suggested_unids);
                setAkteDisambiguationStatus('unambiguous');
              } else {
                setAkteDisambiguationStatus('ambiguous');
              }
            } else {
              setAkteDisambiguationStatus('ambiguous');
            }
          })
          .catch(() => {
            setAkteDisambiguationStatus('ambiguous');
          })
          .finally(() => setSaveToAkteResolveLoading(false));
      })
      .catch(() => {
        setAutoResolvedByEmail([]);
        setSaveToAkteStudentUnids([]);
        setAkteDisambiguationStatus('idle');
        setSaveToAkteResolveLoading(false);
      });
  }, [isOpen, saveToAkte, saveToAkteMode, selectedRecipients, replyToRecipientsDisplay, replyCcRecipientsDisplay, removedReplyDisplayEmails]);

  // Early return AFTER all hooks are declared
  if (!isOpen) return null;

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Save manually entered email to personal address book
  const saveEmailToAddressBook = async (email: string, name?: string) => {
    try {
      const response = await api.post('/address-book', {
        name: name || email.split('@')[0],
        email: email.toLowerCase(),
        contact_type: 'personal'
      });
      if (response.data?.success) {
        console.log('[EmailModal] Saved email to address book:', email);
      }
    } catch (error: any) {
      // Ignore if already exists (409) or other errors
      if (error?.response?.status !== 409) {
        console.error('[EmailModal] Error saving email to address book:', error);
      }
    }
  };

  const addRecipient = async (contact: any, type: 'to' | 'cc' | 'bcc', isManualEntry = false) => {
    const email = contact.email?.toLowerCase() || contact.toLowerCase();
    const name = contact.name || email.split('@')[0];
    
    // Validate email
    if (!isValidEmail(email)) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    // Check if already added
    if (selectedRecipients.some(r => r.email.toLowerCase() === email)) {
      toast.error('Diese Adresse wurde bereits hinzugefügt');
      return;
    }

    // If manually entered (not from address book), save to personal address book
    if (isManualEntry) {
      await saveEmailToAddressBook(email, name);
    }

    setSelectedRecipients([...selectedRecipients, { 
      email, 
      name,
      type 
    }]);
    setAddressBookSearch('');
    setShowAddressBookResults(false);
  };

  const removeRecipient = (email: string) => {
    setSelectedRecipients(selectedRecipients.filter(r => r.email !== email));
    setRemovedReplyDisplayEmails(prev => new Set(prev).add(email.toLowerCase()));
  };

  // Handle Enter key in recipient input fields
  const handleRecipientKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>, type: 'to' | 'cc' | 'bcc') => {
    if (e.key === 'Enter' && addressBookSearch.trim()) {
      e.preventDefault();
      const email = addressBookSearch.trim();
      if (isValidEmail(email)) {
        await addRecipient(email, type, true);
      } else {
        toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      }
    }
  };

  const handleHtmlFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    htmlEditorRef.current?.focus();
    if (htmlEditorRef.current) setBody(htmlEditorRef.current.innerHTML);
  };

  // Apply template with variable replacement
  const applyTemplate = (template: any) => {
    if (!template) return;

    let subjectText = template.subject_template || '';
    let bodyText = template.body_template || '';

    // Get template variables
    const variables = template.variables ? (typeof template.variables === 'string' ? JSON.parse(template.variables) : template.variables) : {};

    // Replace variables
    const replacements: Record<string, string> = {
      student_name: studentName || '{{student_name}}',
      parent_name: parentEmails.length > 0 ? 'Eltern' : '{{parent_name}}',
      teacher_name: fromEmail?.split('@')[0] || '{{teacher_name}}',
      date: new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
      reason: '{{reason}}',
      positive_feedback: '{{positive_feedback}}'
    };

    // Replace all variables in subject and body
    Object.keys(replacements).forEach(key => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      subjectText = subjectText.replace(regex, replacements[key]);
      bodyText = bodyText.replace(regex, replacements[key]);
    });

    setSubject(subjectText);
    setBody(bodyText);
    setSelectedTemplate(template.id);
    if (sendAsHtml && htmlEditorRef.current) {
      const html = (bodyText && bodyText.includes('<') && bodyText.includes('>')) ? bodyText : bodyText.split('\n').map((l: string) => `<p>${l || '<br>'}</p>`).join('');
      htmlEditorRef.current.innerHTML = html || '';
    }

    // Mark template as used
    if (template.id) {
      api.post(`/email-client-advanced/templates/${template.id}/use`).catch(console.error);
    }

    toast.success(`Vorlage "${template.name}" angewendet`);
  };

  const handleSend = async (skipAnalysis = false) => {
    // If there's a valid email in any search field, add it as recipient
    if (addressBookSearch.trim() && isValidEmail(addressBookSearch.trim())) {
      const fieldType = activeRecipientField || (activeStudentUnid || isSeparateStudentBatch ? 'cc' : 'to');
      // Check if this email is already in selectedRecipients
      if (!selectedRecipients.some(r => r.email.toLowerCase() === addressBookSearch.trim().toLowerCase())) {
        await addRecipient(addressBookSearch.trim(), fieldType, true);
        // Wait a bit for state to update
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    // Build recipients from selectedRecipients or fallback to toAddress/studentUnid
    let toRecipients = selectedRecipients.filter(r => r.type === 'to').map(r => r.email);
    let ccRecipients = selectedRecipients.filter(r => r.type === 'cc').map(r => r.email);
    const bccRecipients = selectedRecipients.filter(r => r.type === 'bcc').map(r => r.email);
    if (toRecipients.length === 0 && replyToRecipientsDisplay.length > 0) {
      toRecipients = replyToRecipientsDisplay.filter(r => !removedReplyDisplayEmails.has(r.email.toLowerCase())).map(r => r.email);
    }
    if (ccRecipients.length === 0 && replyCcRecipientsDisplay.length > 0) {
      ccRecipients = replyCcRecipientsDisplay.filter(r => !removedReplyDisplayEmails.has(r.email.toLowerCase())).map(r => r.email);
    }
    // Fallback: Use toAddress if no recipients selected (for backward compatibility)
    let recipientTo = toRecipients.length > 0 ? toRecipients.join(',') : (to || toAddress);
    
    // If still no recipient and there's a valid email in search field, use it
    if (!recipientTo && addressBookSearch.trim() && isValidEmail(addressBookSearch.trim()) && (activeRecipientField === 'to' || !activeRecipientField)) {
      recipientTo = addressBookSearch.trim();
    }
    
    if (!recipientTo && !activeStudentUnid && !isSeparateStudentBatch && selectedRecipients.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Empfänger aus');
      return;
    }
    const bodyToSend = (sendAsHtml && htmlEditorRef.current) ? htmlEditorRef.current.innerHTML : body;
    const scheduledAtValue = scheduleLater ? (scheduledAt || getNextMorningValue()) : undefined;
    if (scheduleLater && !scheduledAt) {
      setScheduledAt(scheduledAtValue || '');
    }

    // Inhaltsanalyse vor dem Versand (außer bei "Trotzdem senden", wenn vom User deaktiviert oder alle Empfänger ausgenommen)
    const recipientEmailsRaw: string[] = [];
    if (recipientTo) {
      recipientEmailsRaw.push(...parseEmailAddresses(recipientTo).map((x) => x.email.toLowerCase()));
    }
    if (activeStudentUnid) {
      for (const raw of parentEmails) {
        const parts = String(raw || '')
          .split(/[,;]/)
          .map((t) => t.trim())
          .filter(Boolean);
        for (const p of parts) {
          const parsed = parseEmailAddresses(p);
          if (parsed.length) recipientEmailsRaw.push(...parsed.map((x) => x.email.toLowerCase()));
          else if (p.includes('@')) recipientEmailsRaw.push(p.toLowerCase());
        }
      }
      for (const r of selectedRecipients) {
        recipientEmailsRaw.push(r.email.toLowerCase());
      }
    }
    const recipientEmails = [...new Set(recipientEmailsRaw)];
    const allRecipientsExcluded = recipientEmails.length > 0 && recipientEmails.every((e) => contentCheckExcludedRecipients.includes(e));
    if (!skipAnalysis && emailContentCheckEnabled && !allRecipientsExcluded) {
      setContentWarningAnalyzing(true);
      try {
        const analyzeRes = await api.post('/email/analyze-content', { subject, body: bodyToSend }, { timeout: 15000 });
        if (analyzeRes.data?.success && analyzeRes.data?.data?.warning) {
          setContentWarningReason(analyzeRes.data.data.reason || 'Der Inhalt könnte problematisch wirken.');
          setContentWarningSuggestions(analyzeRes.data.data.suggestions || []);
          setContentWarningRecipientEmails(recipientEmails);
          setExcludeFromFutureCheck(false);
          pendingSendRef.current = () => handleSend(true);
          setShowContentWarningModal(true);
          setContentWarningAnalyzing(false);
          return;
        }
      } catch {
        // Bei Fehler der Analyse: trotzdem senden erlauben
      }
      setContentWarningAnalyzing(false);
    }

    if (isSeparateStudentBatch) {
      if (scheduleLater) {
        toast.error('Geplanter Versand ist bei Gruppen-E-Mails nicht verfügbar');
        return;
      }
      if (attachments.length > 0) {
        toast.error('Anhänge werden bei Gruppen-E-Mails derzeit nicht unterstützt');
        return;
      }
      setLoading(true);
      try {
        let successCount = 0;
        let failureCount = 0;
        for (const unid of effectiveStudentUnids) {
          try {
            const payload: Record<string, unknown> = {
              subject,
              body: bodyToSend,
              isHtml: sendAsHtml,
              recipientName: studentName,
              translateToFamilyLanguage: translateToFamilyLanguage && familyLanguage,
              familyLanguage: familyLanguage,
              ccClassTeacher,
              ccSchoolLeadership,
              fromEmail,
              studentUnid: unid
            };
            if (ccRecipients.length > 0) {
              payload.cc = ccRecipients.join(',');
            }
            const allBccStudent = [...bccRecipients];
            if (bccSender && fromEmail) {
              allBccStudent.push(fromEmail);
            }
            if (allBccStudent.length > 0) {
              payload.bcc = allBccStudent.join(',');
            }
            const batchRes = await api.post('/email/send', payload);
            if (batchRes.data?.success) {
              successCount += 1;
            } else {
              failureCount += 1;
            }
          } catch {
            failureCount += 1;
          }
        }
        if (successCount > 0) {
          toast.success(
            `${successCount} separate E-Mail(s) versendet${failureCount > 0 ? `, ${failureCount} fehlgeschlagen` : ''}`
          );
          onClose();
        } else {
          toast.error('Keine E-Mails konnten versendet werden');
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      // Prepare attachments as FormData if there are any
      let res;
      if (attachments.length > 0) {
        const formData = new FormData();
        formData.append('subject', subject);
        formData.append('body', bodyToSend);
        formData.append('isHtml', String(sendAsHtml));
        if (studentName) formData.append('recipientName', studentName);
        if (translateToFamilyLanguage && familyLanguage) {
          formData.append('translateToFamilyLanguage', 'true');
          formData.append('familyLanguage', familyLanguage);
        }
        formData.append('ccClassTeacher', String(ccClassTeacher));
        formData.append('ccSchoolLeadership', String(ccSchoolLeadership));
        if (fromEmail) formData.append('fromEmail', fromEmail);
        if (scheduleLater && scheduledAtValue) {
          formData.append('scheduledAt', scheduledAtValue);
          if (scheduledEmailTimezone) formData.append('timezone', scheduledEmailTimezone);
        }
        
        if (activeStudentUnid) {
          formData.append('studentUnid', activeStudentUnid);
          if (ccRecipients.length > 0) {
            formData.append('cc', ccRecipients.join(','));
          }
          const allBccStudent = [...bccRecipients];
          if (bccSender && fromEmail) {
            allBccStudent.push(fromEmail);
          }
          if (allBccStudent.length > 0) {
            formData.append('bcc', allBccStudent.join(','));
          }
        } else {
          formData.append('to', recipientTo);
          if (ccRecipients.length > 0) {
            formData.append('cc', ccRecipients.join(','));
          }
          const allBcc = [...bccRecipients];
          if (bccSender && fromEmail) {
            allBcc.push(fromEmail);
          }
          if (allBcc.length > 0) {
            formData.append('bcc', allBcc.join(','));
          }
        }
        
        // Add attachments
        attachments.forEach((attachment) => {
          formData.append('attachments', attachment.file);
        });
        
        const endpoint = scheduleLater ? '/email/schedule-with-attachments' : '/email/send-with-attachments';
        res = await api.post(endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        const payload: any = {
          subject,
          body: bodyToSend,
          isHtml: sendAsHtml,
          recipientName: studentName,
          translateToFamilyLanguage: translateToFamilyLanguage && familyLanguage,
          familyLanguage: familyLanguage,
          ccClassTeacher,
          ccSchoolLeadership,
          fromEmail,
          scheduledAt: scheduledAtValue
        };
        if (scheduleLater && scheduledEmailTimezone) {
          payload.timezone = scheduledEmailTimezone;
        }
        
        if (activeStudentUnid) {
          payload.studentUnid = activeStudentUnid;
          if (ccRecipients.length > 0) {
            payload.cc = ccRecipients.join(',');
          }
          const allBccStudent = [...bccRecipients];
          if (bccSender && fromEmail) {
            allBccStudent.push(fromEmail);
          }
          if (allBccStudent.length > 0) {
            payload.bcc = allBccStudent.join(',');
          }
        } else {
          payload.to = recipientTo;
          if (ccRecipients.length > 0) {
            payload.cc = ccRecipients.join(',');
          }
          const allBcc = [...bccRecipients];
          if (bccSender && fromEmail) {
            allBcc.push(fromEmail);
          }
          if (allBcc.length > 0) {
            payload.bcc = allBcc.join(',');
          }
        }
        
        const endpoint = scheduleLater ? '/email/schedule' : '/email/send';
        res = await api.post(endpoint, payload);
      }
      if (res.data?.success) {
        if (scheduleLater) {
          toast.success('E-Mail wurde zum Versand geplant');
          onClose();
          return;
        }
        if (res.data.data?.sentToParents) {
          const result = res.data.data;
          const successCount = result.results?.filter((r: any) => r.success).length || 0;
          const failureCount = result.results?.filter((r: any) => !r.success).length || 0;
          let message = `${successCount} E-Mail(s) an Eltern gesendet`;
          if (failureCount > 0) {
            message += `, ${failureCount} fehlgeschlagen`;
          }
          if (translateToFamilyLanguage && familyLanguage) {
            message += ` (übersetzt in ${familyLanguage})`;
          }
          toast.success(message);
        } else {
          let message = 'E-Mail wurde versendet';
          if (translateToFamilyLanguage && familyLanguage) {
            message += ` (übersetzt in ${familyLanguage})`;
          }
          toast.success(message);
        }
        if (saveToAkte && saveToAkteStudentUnids.length > 0) {
          try {
            const bodyForAkte = (sendAsHtml && htmlEditorRef.current) ? htmlEditorRef.current.innerHTML : bodyToSend;
            await api.post('/email-client/composed-email/add-to-student-file', {
              subject,
              body: bodyForAkte,
              isHtml: sendAsHtml,
              studentUnids: saveToAkteStudentUnids,
              fromEmail: fromEmail || undefined,
              toEmail: recipientTo || undefined
            });
            toast.success(`E-Mail in ${saveToAkteStudentUnids.length} private${saveToAkteStudentUnids.length === 1 ? ' Akte' : ' Akten'} abgelegt`);
          } catch (err: any) {
            toast.error('E-Mail gesendet, aber Ablage in Akte fehlgeschlagen: ' + (err?.response?.data?.error || err?.message));
          }
        }
        if (imapDraftUid && draftsMailbox) {
          try {
            await api.delete(`/email-client/email/${imapDraftUid}`, {
              params: { box: draftsMailbox, ...(fromEmail ? { emailAddress: fromEmail } : {}) }
            });
          } catch {
            // non-critical
          }
        }
        onClose();
      } else {
        toast.error(res.data?.error || 'E-Mail-Versand fehlgeschlagen');
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      const details = Array.isArray(data?.details) ? data.details : [];
      const firstDetailMsg = details[0]?.msg ?? details[0]?.message;
      const msg =
        firstDetailMsg ||
        data?.error ||
        data?.message ||
        e?.message ||
        'E-Mail-Versand fehlgeschlagen';
      const looksLikeMissingCreds =
        status === 400 &&
        typeof msg === 'string' &&
        (msg.toLowerCase().includes('kein e-mail-konto') ||
          msg.toLowerCase().includes('no email credentials') ||
          msg.toLowerCase().includes('personal') && msg.toLowerCase().includes('einstellungen'));

      if (looksLikeMissingCreds) {
        toast.error('Kein E-Mail-Konto konfiguriert. Bitte hinterlegen Sie es jetzt.');
        setShowEmailCredentialModal(true);
        return;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };


  const handleImproveEmail = async () => {
    if (!body.trim()) {
      toast.error('Bitte geben Sie zuerst eine E-Mail ein, die verbessert werden soll');
      return;
    }
    setAiLoading(true);
    try {
      if (activeStudentUnid) {
        // Mit Schüler: Entwurf aus Kontext generieren
        const res = await api.post(
          '/email/generate-draft',
          { studentUnid: activeStudentUnid, keywords: body, context },
          { timeout: 30000 }
        );
        if (res.data?.success) {
          const draft = res.data.data;
          setSubject(draft.subject || subject);
          setBody(draft.body || body);
          toast.success('E-Mail wurde verbessert');
        } else {
          toast.error(
            improveAiErrorUserMessage({ response: { data: res.data } }, res.data?.error || 'E-Mail-Verbesserung fehlgeschlagen')
          );
        }
      } else {
        // Ohne Schüler: nur Formulierung verbessern (improve-content)
        const bodyToImprove = (sendAsHtml && htmlEditorRef.current) ? htmlEditorRef.current.innerHTML : body;
        const res = await api.post('/email/improve-content', { subject, body: bodyToImprove }, { timeout: 20000 });
        if (res.data?.success) {
          const { subject: newSubject, body: newBody } = res.data.data;
          setSubject(newSubject);
          setBody(newBody);
          if (sendAsHtml && htmlEditorRef.current) {
            const html = newBody.includes('<') && newBody.includes('>') ? newBody : newBody.split('\n').map((l: string) => `<p>${l || '<br>'}</p>`).join('');
            htmlEditorRef.current.innerHTML = html || '';
          }
          toast.success('Formulierung wurde verbessert.');
        } else {
          toast.error(
            improveAiErrorUserMessage({ response: { data: res.data } }, res.data?.error || 'Verbesserung fehlgeschlagen')
          );
        }
      }
    } catch (e: unknown) {
      toast.error(improveAiErrorUserMessage(e, 'E-Mail-Verbesserung fehlgeschlagen'));
    } finally {
      setAiLoading(false);
    }
  };

  const handleImproveFromWarning = async () => {
    const bodyToImprove = (sendAsHtml && htmlEditorRef.current) ? htmlEditorRef.current.innerHTML : body;
    setShowContentWarningModal(false);
    setContentWarningReason(null);
    setContentWarningSuggestions([]);
    setAiLoading(true);
    try {
      const res = await api.post('/email/improve-content', { subject, body: bodyToImprove }, { timeout: 20000 });
      if (res.data?.success) {
        const { subject: newSubject, body: newBody } = res.data.data;
        setSubject(newSubject);
        setBody(newBody);
        if (sendAsHtml && htmlEditorRef.current) {
          const html = newBody.includes('<') && newBody.includes('>') ? newBody : newBody.split('\n').map((l: string) => `<p>${l || '<br>'}</p>`).join('');
          htmlEditorRef.current.innerHTML = html || '';
        }
        toast.success('E-Mail wurde verbessert. Bitte prüfen und erneut senden.');
      } else {
        toast.error(
          improveAiErrorUserMessage({ response: { data: res.data } }, res.data?.error || 'Verbesserung fehlgeschlagen')
        );
      }
    } catch (e: unknown) {
      toast.error(improveAiErrorUserMessage(e, 'Verbesserung fehlgeschlagen'));
    } finally {
      setAiLoading(false);
    }
  };

  const buildDraftRecipients = () => {
    let toRecipients = selectedRecipients.filter((r) => r.type === 'to').map((r) => r.email);
    let ccRecipients = selectedRecipients.filter((r) => r.type === 'cc').map((r) => r.email);
    const bccRecipients = selectedRecipients.filter((r) => r.type === 'bcc').map((r) => r.email);
    if (toRecipients.length === 0 && replyToRecipientsDisplay.length > 0) {
      toRecipients = replyToRecipientsDisplay
        .filter((r) => !removedReplyDisplayEmails.has(r.email.toLowerCase()))
        .map((r) => r.email);
    }
    if (ccRecipients.length === 0 && replyCcRecipientsDisplay.length > 0) {
      ccRecipients = replyCcRecipientsDisplay
        .filter((r) => !removedReplyDisplayEmails.has(r.email.toLowerCase()))
        .map((r) => r.email);
    }
    let recipientTo = toRecipients.length > 0 ? toRecipients.join(',') : (to || toAddress);
    if (!recipientTo && activeStudentUnid && parentEmails.length > 0) {
      recipientTo = parentEmails.join(',');
    }
    return {
      to: recipientTo || undefined,
      cc: ccRecipients.length > 0 ? ccRecipients.join(',') : undefined,
      bcc: bccRecipients.length > 0 ? bccRecipients.join(',') : undefined
    };
  };

  const loadImapDrafts = async () => {
    const box = draftsMailbox || 'Drafts';
    setDraftsLoading(true);
    try {
      const res = await api.get('/email-client/inbox', {
        params: {
          box,
          limit: 25,
          offset: 0,
          forceSync: true,
          ...(fromEmail ? { emailAddress: fromEmail } : {})
        }
      });
      const emails = res.data?.data || [];
      setImapDrafts(
        emails.map((e: { uid: number; headers?: { subject?: string; date?: string | Date; to?: string; cc?: string } }) => ({
          uid: e.uid,
          subject: e.headers?.subject || '(Ohne Betreff)',
          date: e.headers?.date ? new Date(e.headers.date) : new Date(),
          to: e.headers?.to,
          cc: e.headers?.cc
        }))
      );
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
      toast.error(e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Entwürfe konnten nicht geladen werden');
      setImapDrafts([]);
    } finally {
      setDraftsLoading(false);
    }
  };

  const applyHtmlBodyToEditor = (bodyContent: string, asHtml: boolean) => {
    if (!asHtml) return;
    setTimeout(() => {
      if (htmlEditorRef.current) {
        const html =
          bodyContent && bodyContent.includes('<') && bodyContent.includes('>')
            ? bodyContent
            : (bodyContent || '')
                .split('\n')
                .map((l: string) => `<p>${(l || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`)
                .join('');
        htmlEditorRef.current.innerHTML = html || '';
      }
    }, 0);
  };

  const recipientsFromHeader = (toHeader?: string, ccHeader?: string) => {
    const recipients: Array<{ email: string; name: string; type: 'to' | 'cc' | 'bcc' }> = [];
    if (toHeader) {
      for (const p of parseEmailAddresses(toHeader)) {
        recipients.push({ email: p.email, name: p.name || p.email, type: 'to' });
      }
    }
    if (ccHeader) {
      for (const p of parseEmailAddresses(ccHeader)) {
        recipients.push({ email: p.email, name: p.name || p.email, type: 'cc' });
      }
    }
    return recipients;
  };

  const applyImapDraft = async (draft: ImapDraftListItem, folderOverride?: string, options?: { silent?: boolean }) => {
    const box = folderOverride || draftsMailbox || 'Drafts';
    try {
      const res = await api.get(`/email-client/email/${draft.uid}/body`, {
        params: { box, ...(fromEmail ? { emailAddress: fromEmail } : {}) }
      });
      const data = res.data?.data;
      const htmlBody = data?.body_html || '';
      const textBody = data?.body_text || '';
      const useHtml = !!htmlBody;
      const bodyContent = useHtml ? htmlBody : textBody;

      setSubject(draft.subject === '(Kein Betreff)' ? '' : draft.subject);
      setBody(bodyContent);
      setSendAsHtml(useHtml);
      setSelectedRecipients(recipientsFromHeader(draft.to, draft.cc));
      setImapDraftUid(draft.uid);
      if (folderOverride) setDraftsMailbox(folderOverride);
      setShowDraftsMenu(false);
      applyHtmlBodyToEditor(bodyContent, useHtml);
      if (!options?.silent) toast.success('Entwurf geladen');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
      toast.error(e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Entwurf konnte nicht geladen werden');
    }
  };

  const deleteImapDraft = async (uid: number) => {
    const box = draftsMailbox || 'Drafts';
    try {
      await api.delete(`/email-client/email/${uid}`, {
        params: { box, ...(fromEmail ? { emailAddress: fromEmail } : {}) }
      });
      if (imapDraftUid === uid) setImapDraftUid(null);
      await loadImapDrafts();
      toast.success('Entwurf gelöscht');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
      toast.error(e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Entwurf konnte nicht gelöscht werden');
    }
  };

  const handleSaveDraft = async () => {
    if (attachments.length > 0) {
      toast('Anhänge werden in Entwürfen noch nicht unterstützt und werden nicht mitgespeichert.', { duration: 5000 });
    }
    const bodySnap = sendAsHtml && htmlEditorRef.current ? htmlEditorRef.current.innerHTML : body;
    const { to: draftTo, cc: draftCc, bcc: draftBcc } = buildDraftRecipients();
    setSavingDraft(true);
    try {
      const res = await api.post('/email-client/save-draft', {
        subject,
        body: bodySnap,
        isHtml: sendAsHtml,
        to: draftTo,
        cc: draftCc,
        bcc: draftBcc,
        fromEmail: fromEmail || undefined,
        draftUid: imapDraftUid || undefined,
        draftsFolder: draftsMailbox || undefined,
        meta: {
          context,
          studentUnid: activeStudentUnid,
          studentUnids: isSeparateStudentBatch ? effectiveStudentUnids : undefined,
          studentName,
          sendAsHtml,
          selectedRecipients,
          scheduleLater,
          scheduledAt,
          saveToAkte,
          saveToAkteMode,
          saveToAkteStudentUnids,
          saveToAkteStudentInfo,
          ccClassTeacher,
          ccSchoolLeadership,
          bccSender,
          translateToFamilyLanguage
        }
      });
      if (res.data?.success) {
        const uid = res.data?.data?.uid;
        if (typeof uid === 'number') setImapDraftUid(uid);
        if (res.data?.data?.folder) setDraftsMailbox(res.data.data.folder);
        toast.success('Entwurf im Postfach gespeichert');
        setShowDraftsMenu(false);
      } else {
        toast.error(res.data?.error || 'Entwurf konnte nicht gespeichert werden');
      }
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string; error?: string; code?: string } }; message?: string };
      const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Entwurf konnte nicht gespeichert werden';
      if (e?.response?.status === 400 && e?.response?.data?.code === 'NO_EMAIL_CREDENTIALS') {
        toast.error('Kein E-Mail-Konto konfiguriert. Bitte hinterlegen Sie es jetzt.');
        setShowEmailCredentialModal(true);
        return;
      }
      toast.error(msg);
    } finally {
      setSavingDraft(false);
    }
  };

  const composeHeaderActions = (
    <>
      <div className="relative" ref={draftsMenuRef}>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            setShowDraftsMenu((v) => {
              const next = !v;
              if (next) loadImapDrafts();
              return next;
            });
          }}
          title="Entwürfe aus dem Postfach (IMAP)"
          className="gap-1"
        >
          <FolderOpen className="h-4 w-4" />
          <span className="hidden sm:inline">Entwürfe</span>
        </Button>
        {showDraftsMenu && (
          <div className="absolute right-0 z-[70] mt-1 w-72 max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white text-left shadow-lg dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 p-2 dark:border-gray-800">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Entwürfe</span>
              <Button type="button" variant="ghost" size="xs" onClick={() => setShowDraftsMenu(false)}>
                Schließen
              </Button>
            </div>
            <div>
              {draftsLoading ? (
                <div className="p-3 text-sm text-gray-500 dark:text-gray-400">Entwürfe werden geladen…</div>
              ) : imapDrafts.length === 0 ? (
                <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
                  Keine Entwürfe im Postfach. Gespeicherte Entwürfe erscheinen auch in anderen E-Mail-Programmen.
                </div>
              ) : (
                imapDrafts.map((d) => (
                  <div key={d.uid} className="flex items-start gap-2 border-b border-gray-50 p-2 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/60">
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto min-w-0 flex-1 flex-col items-start gap-0.5 py-1.5 text-left font-normal"
                      onClick={() => applyImapDraft(d)}
                    >
                      <span className="w-full truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {d.subject || '(Ohne Betreff)'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {d.date.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      className="shrink-0 text-gray-400 hover:text-red-600 dark:text-gray-500"
                      title="Entwurf löschen"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteImapDraft(d.uid);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={handleSaveDraft}
        disabled={savingDraft}
        title="Aktuelle E-Mail als IMAP-Entwurf speichern"
        className="gap-1"
      >
        <Save className="h-4 w-4" />
        <span className="hidden sm:inline">{savingDraft ? 'Speichern…' : 'Speichern'}</span>
      </Button>
    </>
  );

  const composeFooter = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <Button type="button" variant="outline" size="sm" onClick={onClose}>
        Abbrechen
      </Button>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          variant="primary"
          className={scheduleLater ? 'ring-2 ring-primary-300 dark:ring-primary-500/40' : ''}
          onClick={() => {
            const next = !scheduleLater;
            setScheduleLater(next);
            if (next && !scheduledAt) fetchNextMorningAndSet();
          }}
        >
          <Clock className="h-4 w-4" />
          Später senden
        </Button>
        {scheduleLater && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="input-field max-w-[12rem] min-h-[2.25rem] py-2 text-sm"
            />
            <Button type="button" size="sm" variant="outline" onClick={() => fetchNextMorningAndSet()}>
              Morgen 08:00
            </Button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={handleImproveEmail}
          disabled={loading || aiLoading || !body.trim()}
          title={!body.trim() ? 'Bitte geben Sie zuerst eine E-Mail ein' : 'E-Mail mit KI verbessern'}
        >
          {aiLoading ? <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <span>✨</span>}
          <span className="ml-1">Verbessern</span>
        </Button>
        <Button
          type="button"
          size="sm"
          variant="primary"
          onClick={() => handleSend()}
          disabled={
            loading ||
            contentWarningAnalyzing ||
            !subject.trim() ||
            !body.trim() ||
            (!toAddress && !to && !activeStudentUnid && !isSeparateStudentBatch && selectedRecipients.length === 0 && !isValidEmail(addressBookSearch.trim()))
          }
          title={
            !subject.trim() || !body.trim()
              ? 'Bitte geben Sie Betreff und Nachricht ein'
              : toAddress || to || activeStudentUnid || isSeparateStudentBatch || selectedRecipients.length > 0 || isValidEmail(addressBookSearch.trim())
                ? 'E-Mail senden'
                : 'Bitte geben Sie eine Empfänger-Adresse ein'
          }
        >
          {loading || contentWarningAnalyzing ? (
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          <span className="ml-1">{contentWarningAnalyzing ? 'Prüfen…' : 'Senden'}</span>
        </Button>
      </div>
    </div>
  );

  return (
    <>
    <Modal
      isOpen={showContentWarningModal}
      onClose={() => {
        setShowContentWarningModal(false);
        setContentWarningReason(null);
        setContentWarningSuggestions([]);
        setContentWarningRecipientEmails([]);
        setExcludeFromFutureCheck(false);
        pendingSendRef.current = null;
      }}
      title="Hinweis zur E-Mail"
      description="Nur ein kurzer Hinweis – Sie entscheiden selbst."
      size="md"
      backdropClassName="z-[60]"
      footer={
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="primary"
            onClick={async () => {
              if (excludeFromFutureCheck && contentWarningRecipientEmails.length > 0) {
                try {
                  const merged = [...new Set([...contentCheckExcludedRecipients, ...contentWarningRecipientEmails])];
                  await api.put('/settings/email-content-check-exclusions', { excludedRecipients: merged });
                  await refetchEmailContentCheck();
                } catch (_) {
                  toast.error('Ausnahme konnte nicht gespeichert werden');
                }
              }
              pendingSendRef.current?.();
              pendingSendRef.current = null;
              setShowContentWarningModal(false);
              setContentWarningReason(null);
              setContentWarningSuggestions([]);
              setContentWarningRecipientEmails([]);
              setExcludeFromFutureCheck(false);
            }}
          >
            Trotzdem senden
          </Button>
          <Button type="button" variant="secondary" onClick={handleImproveFromWarning} disabled={aiLoading}>
            {aiLoading ? 'Verbessern…' : 'E-Mail verbessern'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setShowContentWarningModal(false);
              setContentWarningReason(null);
              setContentWarningSuggestions([]);
              setContentWarningRecipientEmails([]);
              setExcludeFromFutureCheck(false);
              pendingSendRef.current = null;
            }}
          >
            Abbrechen
          </Button>
        </div>
      }
    >
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-950/50">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <p className="text-sm text-gray-800 dark:text-gray-200">{contentWarningReason}</p>
          {contentWarningSuggestions.length > 0 && (
            <ul className="list-inside list-disc space-y-1 text-sm text-gray-600 dark:text-gray-400">
              {contentWarningSuggestions.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          )}
          {contentWarningRecipientEmails.length > 0 && (
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={excludeFromFutureCheck}
                onChange={(e) => setExcludeFromFutureCheck(e.target.checked)}
                className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {contentWarningRecipientEmails.length === 1
                  ? 'Diesen Empfänger von künftiger Prüfung ausnehmen (z. B. weil man sich besser kennt)'
                  : 'Diese Empfänger von künftiger Prüfung ausnehmen (z. B. weil man sich besser kennt)'}
              </span>
            </label>
          )}
        </div>
      </div>
    </Modal>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="E-Mail verfassen"
      description={studentName ? <span className="text-gray-600 dark:text-gray-400">An: {studentName}</span> : undefined}
      size="xl"
      positionClassName="items-end justify-center sm:items-center"
      panelClassName="max-h-[min(92dvh,calc(100dvh-1rem))] sm:max-h-[min(92dvh,920px)]"
      backdropClassName="overflow-y-auto overscroll-y-contain"
      headerActions={composeHeaderActions}
      footer={composeFooter}
    >
      <div
        className="relative space-y-3"
        onDragEnter={handleComposeDragEnter}
        onDragOver={handleComposeDragOver}
        onDragLeave={handleComposeDragLeave}
        onDrop={handleComposeDrop}
      >
            {isDragging && (
              <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg border-2 border-dashed border-primary-500 bg-primary-50/95 dark:bg-primary-950/90 pointer-events-none">
                <div className="flex flex-col items-center gap-2 text-primary-700 dark:text-primary-300">
                  <Paperclip className="w-8 h-8" />
                  <span className="text-sm font-medium">Dateien hier ablegen</span>
                </div>
              </div>
            )}
            {/* Optionen: eine kompakte Zeile; BCC nur wenn kein IMAP "Gesendet" vorhanden */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {!hasSentMailbox && (
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    id="bcc-sender"
                    type="checkbox"
                    checked={bccSender}
                    onChange={(e) => setBccSender(e.target.checked)}
                    className="email-compose-checkbox"
                  />
                  <span className="email-compose-option-label">BCC an mich{fromEmail ? ` (${fromEmail})` : ''}</span>
                </label>
              )}
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  id="send-html"
                  type="checkbox"
                  checked={sendAsHtml}
                  onChange={(e) => {
                    const next = e.target.checked;
                    if (!next && htmlEditorRef.current) {
                      setBody(htmlEditorRef.current.innerText || htmlEditorRef.current.textContent || body);
                    }
                    setSendAsHtml(next);
                  }}
                  className="email-compose-checkbox"
                />
                <span className="email-compose-option-label">HTML</span>
              </label>
              <label className={`flex items-center gap-1.5 ${!familyLanguage ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  id="translate-family-language"
                  type="checkbox"
                  checked={translateToFamilyLanguage}
                  onChange={(e) => setTranslateToFamilyLanguage(e.target.checked)}
                  disabled={!familyLanguage}
                  className={`email-compose-checkbox ${!familyLanguage ? 'opacity-50' : ''}`}
                />
                <span className={`email-compose-option-label ${!familyLanguage ? 'text-gray-400 dark:text-gray-500' : ''}`}>
                  Übersetzen{familyLanguage ? ` (${familyLanguage})` : ''}
                </span>
              </label>
              <label className={`flex items-center gap-1.5 ${!classTeacherEmail ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  id="cc-class-teacher"
                  type="checkbox"
                  checked={ccClassTeacher}
                  onChange={(e) => setCcClassTeacher(e.target.checked)}
                  disabled={!classTeacherEmail}
                  className={`email-compose-checkbox ${!classTeacherEmail ? 'opacity-50' : ''}`}
                />
                <span className={`email-compose-option-label ${!classTeacherEmail ? 'text-gray-400 dark:text-gray-500' : ''}`}>KL in CC</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  id="cc-school-leadership"
                  type="checkbox"
                  checked={ccSchoolLeadership}
                  onChange={(e) => setCcSchoolLeadership(e.target.checked)}
                  className="email-compose-checkbox"
                />
                <span className="email-compose-option-label">Schulleitung in CC</span>
              </label>
            </div>
            {/* Recipient Selection */}
          {(activeStudentUnid || isSeparateStudentBatch) ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {isSeparateStudentBatch ? 'Separate E-Mails an' : 'An Eltern von'}
                </label>
                <input
                  value={
                    isSeparateStudentBatch
                      ? `${effectiveStudentUnids.length} Schüler (je E-Mail an die hinterlegten Elternadressen)`
                      : parentEmailsLoading
                      ? 'Lade E-Mail-Adressen...'
                      : parentEmails.length > 0
                        ? (parentEmails.length === 1
                            ? parentEmails[0]
                            : `${parentEmails.join('; ')} (${parentEmails.length} Adressen)`)
                        : 'Keine E-Mail-Adressen gefunden'
                  }
                  readOnly
                  className="input-field bg-gray-50 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Optional: Kollegium oder beliebige E-Mail-Adresse als CC/BCC (Enter zum Übernehmen).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" ref={addressBookRef}>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                    <Users className="w-3 h-3" />
                    CC (optional)
                  </label>
                  <div className="relative">
                    <div className="email-recipient-field">
                      {selectedRecipients.filter(r => r.type === 'cc').map((recipient) => (
                        <Chip
                          key={`stu-cc-${recipient.email}`}
                          variant="success"
                          title={recipient.email}
                          onRemove={() => removeRecipient(recipient.email)}
                          removeLabel={`${recipient.name} (${recipient.email}) entfernen`}
                        >
                          {recipient.name}
                        </Chip>
                      ))}
                      <input
                        type="text"
                        value={activeRecipientField === 'cc' ? addressBookSearch : ''}
                        onChange={(e) => {
                          setAddressBookSearch(e.target.value);
                          setActiveRecipientField('cc');
                          setShowAddressBookResults(true);
                        }}
                        onFocus={() => {
                          setActiveRecipientField('cc');
                          if (addressBookSearch) setShowAddressBookResults(true);
                        }}
                        onKeyDown={(e) => handleRecipientKeyDown(e, 'cc')}
                        placeholder={selectedRecipients.filter(r => r.type === 'cc').length === 0 ? 'Kollegium suchen oder E-Mail…' : ''}
                        className="flex-1 min-w-[120px] border-0 outline-none text-sm bg-transparent text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    {showAddressBookResults && activeRecipientField === 'cc' && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {addressBookLoading ? (
                          <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">Suche...</div>
                        ) : addressBookResults.length === 0 ? (
                          <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">Keine Ergebnisse</div>
                        ) : (
                          addressBookResults.map((contact) => {
                            const photoUrl = contact.contact_type === 'student' && contact.reference_id
                              ? getPhotoUrl(contact.reference_id)
                              : contact.photo_url;
                            return (
                              <button
                                key={`stu-cc-pick-${contact.id}`}
                                type="button"
                                onClick={() => addRecipient(contact, 'cc')}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                              >
                                {photoUrl ? (
                                  <img src={photoUrl} alt={contact.name} className="w-6 h-6 rounded-full object-cover border border-gray-200" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                ) : (
                                  <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                    <User className="w-4 h-4 text-gray-400" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{contact.name}</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{contact.email}</div>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                    <UserPlus className="w-3 h-3" />
                    BCC (optional)
                  </label>
                  <div className="relative">
                    <div className="email-recipient-field">
                      {selectedRecipients.filter(r => r.type === 'bcc').map((recipient) => (
                        <Chip
                          key={`stu-bcc-${recipient.email}`}
                          variant="neutral"
                          title={recipient.email}
                          onRemove={() => removeRecipient(recipient.email)}
                          removeLabel={`${recipient.name} (${recipient.email}) entfernen`}
                        >
                          {recipient.name}
                        </Chip>
                      ))}
                      <input
                        type="text"
                        value={activeRecipientField === 'bcc' ? addressBookSearch : ''}
                        onChange={(e) => {
                          setAddressBookSearch(e.target.value);
                          setActiveRecipientField('bcc');
                          setShowAddressBookResults(true);
                        }}
                        onFocus={() => {
                          setActiveRecipientField('bcc');
                          if (addressBookSearch) setShowAddressBookResults(true);
                        }}
                        onKeyDown={(e) => handleRecipientKeyDown(e, 'bcc')}
                        placeholder={selectedRecipients.filter(r => r.type === 'bcc').length === 0 ? 'BCC: Kollegium oder E-Mail…' : ''}
                        className="flex-1 min-w-[120px] border-0 outline-none text-sm bg-transparent text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    {showAddressBookResults && activeRecipientField === 'bcc' && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {addressBookLoading ? (
                          <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">Suche...</div>
                        ) : addressBookResults.length === 0 ? (
                          <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">Keine Ergebnisse</div>
                        ) : (
                          addressBookResults.map((contact) => {
                            const photoUrl = contact.contact_type === 'student' && contact.reference_id
                              ? getPhotoUrl(contact.reference_id)
                              : contact.photo_url;
                            return (
                              <button
                                key={`stu-bcc-pick-${contact.id}`}
                                type="button"
                                onClick={() => addRecipient(contact, 'bcc')}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                              >
                                {photoUrl ? (
                                  <img src={photoUrl} alt={contact.name} className="w-6 h-6 rounded-full object-cover border border-gray-200" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                ) : (
                                  <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                    <User className="w-4 h-4 text-gray-400" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{contact.name}</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{contact.email}</div>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3" ref={addressBookRef}>
              {/* To Recipients */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                  <User className="w-3 h-3" />
                  An
                </label>
                <div className="relative">
                  <div className="email-recipient-field">
                    {(selectedRecipients.filter(r => r.type === 'to').length > 0
                      ? selectedRecipients.filter(r => r.type === 'to')
                      : replyToRecipientsDisplay.filter(r => !removedReplyDisplayEmails.has(r.email.toLowerCase()))
                    ).map((recipient) => (
                      <Chip
                        key={`to-${recipient.email}`}
                        variant="primary"
                        title={recipient.email}
                        onRemove={() => removeRecipient(recipient.email)}
                        removeLabel={`${recipient.name} (${recipient.email}) entfernen`}
                      >
                        {recipient.name}
                      </Chip>
                    ))}
                    <input
                      type="text"
                      value={activeRecipientField === 'to' ? addressBookSearch : ''}
                      onChange={(e) => {
                        setAddressBookSearch(e.target.value);
                        setActiveRecipientField('to');
                        setShowAddressBookResults(true);
                      }}
                      onFocus={() => {
                        setActiveRecipientField('to');
                        if (addressBookSearch) setShowAddressBookResults(true);
                      }}
                      onKeyDown={(e) => handleRecipientKeyDown(e, 'to')}
                      placeholder={(selectedRecipients.filter(r => r.type === 'to').length > 0 || replyToRecipientsDisplay.filter(r => !removedReplyDisplayEmails.has(r.email.toLowerCase())).length > 0) ? "" : "Suche, Kollegium oder E-Mail eingeben…"}
                      className="flex-1 min-w-[200px] border-0 outline-none text-sm"
                    />
                  </div>
                  {showAddressBookResults && activeRecipientField === 'to' && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {addressBookLoading ? (
                        <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">Suche...</div>
                      ) : addressBookResults.length === 0 ? (
                        <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">Keine Ergebnisse</div>
                      ) : (
                        addressBookResults.map((contact) => {
                          // Generate photo URL for student contacts
                          const photoUrl = contact.contact_type === 'student' && contact.reference_id
                            ? getPhotoUrl(contact.reference_id)
                            : contact.photo_url;
                          
                          return (
                            <button
                              key={contact.id}
                              onClick={() => addRecipient(contact, 'to')}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                            >
                              {photoUrl ? (
                                <img 
                                  src={photoUrl} 
                                  alt={contact.name} 
                                  className="w-6 h-6 rounded-full object-cover border border-gray-200"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                  <User className="w-4 h-4 text-gray-400" />
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="text-sm font-medium">{contact.name}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{contact.email}</div>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* CC Recipients */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                  <Users className="w-3 h-3" />
                  CC
                </label>
                <div className="relative">
                  <div className="email-recipient-field">
                    {(selectedRecipients.filter(r => r.type === 'cc').length > 0
                      ? selectedRecipients.filter(r => r.type === 'cc')
                      : replyCcRecipientsDisplay.filter(r => !removedReplyDisplayEmails.has(r.email.toLowerCase()))
                    ).map((recipient) => (
                      <Chip
                        key={`cc-${recipient.email}`}
                        variant="success"
                        title={recipient.email}
                        onRemove={() => removeRecipient(recipient.email)}
                        removeLabel={`${recipient.name} (${recipient.email}) entfernen`}
                      >
                        {recipient.name}
                      </Chip>
                    ))}
                    <input
                      type="text"
                      value={activeRecipientField === 'cc' ? addressBookSearch : ''}
                      onChange={(e) => {
                        setAddressBookSearch(e.target.value);
                        setActiveRecipientField('cc');
                        setShowAddressBookResults(true);
                      }}
                      onFocus={() => {
                        setActiveRecipientField('cc');
                        if (addressBookSearch) setShowAddressBookResults(true);
                      }}
                      onKeyDown={(e) => handleRecipientKeyDown(e, 'cc')}
                      placeholder={(selectedRecipients.filter(r => r.type === 'cc').length > 0 || replyCcRecipientsDisplay.filter(r => !removedReplyDisplayEmails.has(r.email.toLowerCase())).length > 0) ? "" : "CC: Suche, Kollegium oder E-Mail…"}
                      className="flex-1 min-w-[200px] border-0 outline-none text-sm"
                    />
                  </div>
                  {showAddressBookResults && activeRecipientField === 'cc' && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {addressBookLoading ? (
                        <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">Suche...</div>
                      ) : addressBookResults.length === 0 ? (
                        <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">Keine Ergebnisse</div>
                      ) : (
                        addressBookResults.map((contact) => {
                          // Generate photo URL for student contacts
                          const photoUrl = contact.contact_type === 'student' && contact.reference_id
                            ? getPhotoUrl(contact.reference_id)
                            : contact.photo_url;
                          
                          return (
                            <button
                              key={contact.id}
                              onClick={() => addRecipient(contact, 'cc')}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                            >
                              {photoUrl ? (
                                <img 
                                  src={photoUrl} 
                                  alt={contact.name} 
                                  className="w-6 h-6 rounded-full object-cover border border-gray-200"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                  <User className="w-4 h-4 text-gray-400" />
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="text-sm font-medium">{contact.name}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{contact.email}</div>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* BCC Recipients */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                  <UserPlus className="w-3 h-3" />
                  BCC
                </label>
                <div className="relative">
                  <div className="email-recipient-field">
                    {selectedRecipients.filter(r => r.type === 'bcc').map((recipient) => (
                      <Chip
                        key={`bcc-${recipient.email}`}
                        variant="neutral"
                        title={recipient.email}
                        onRemove={() => removeRecipient(recipient.email)}
                        removeLabel={`${recipient.name} (${recipient.email}) entfernen`}
                      >
                        {recipient.name}
                      </Chip>
                    ))}
                    <input
                      type="text"
                      value={activeRecipientField === 'bcc' ? addressBookSearch : ''}
                      onChange={(e) => {
                        setAddressBookSearch(e.target.value);
                        setActiveRecipientField('bcc');
                        setShowAddressBookResults(true);
                      }}
                      onFocus={() => {
                        setActiveRecipientField('bcc');
                        if (addressBookSearch) setShowAddressBookResults(true);
                      }}
                      onKeyDown={(e) => handleRecipientKeyDown(e, 'bcc')}
                      placeholder={selectedRecipients.filter(r => r.type === 'bcc').length === 0 ? "BCC: Suche, Kollegium oder E-Mail…" : ""}
                      className="flex-1 min-w-[200px] border-0 outline-none text-sm"
                    />
                  </div>
                  {showAddressBookResults && activeRecipientField === 'bcc' && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {addressBookLoading ? (
                        <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">Suche...</div>
                      ) : addressBookResults.length === 0 ? (
                        <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">Keine Ergebnisse</div>
                      ) : (
                        addressBookResults.map((contact) => {
                          // Generate photo URL for student contacts
                          const photoUrl = contact.contact_type === 'student' && contact.reference_id
                            ? getPhotoUrl(contact.reference_id)
                            : contact.photo_url;
                          
                          return (
                            <button
                              key={contact.id}
                              onClick={() => addRecipient(contact, 'bcc')}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                            >
                              {photoUrl ? (
                                <img 
                                  src={photoUrl} 
                                  alt={contact.name} 
                                  className="w-6 h-6 rounded-full object-cover border border-gray-200"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                  <User className="w-4 h-4 text-gray-400" />
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="text-sm font-medium">{contact.name}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{contact.email}</div>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

            {/* In private Akte ablegen (nur bei An/CC/BCC) */}
            {!activeStudentUnid && !isSeparateStudentBatch && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-gray-50/50 dark:bg-gray-800/40 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveToAkte}
                    onChange={(e) => {
                      setSaveToAkte(e.target.checked);
                      if (!e.target.checked) {
                        setSaveToAkteStudentUnids([]);
                        setSaveToAkteStudentInfo({});
                        setAutoResolvedByEmail([]);
                      }
                    }}
                    className="email-compose-checkbox"
                  />
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">E-Mail in private Akte ablegen</span>
                </label>
                {saveToAkte && (
                  <>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" name="akte-mode" checked={saveToAkteMode === 'auto'} onChange={() => setSaveToAkteMode('auto')} className="email-compose-checkbox" />
                        <span className="text-xs text-gray-700 dark:text-gray-300">Automatisch (nach Empfänger)</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" name="akte-mode" checked={saveToAkteMode === 'manual'} onChange={() => setSaveToAkteMode('manual')} className="email-compose-checkbox" />
                        <span className="text-xs text-gray-700 dark:text-gray-300">Manuell</span>
                      </label>
                    </div>
                    {saveToAkteMode === 'auto' && (
                      <>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Eine E-Mail-Adresse kann mehreren Schülern zugeordnet sein. Zuerst werden Empfänger zugeordnet, dann prüft die KI den Inhalt.</p>
                        {(akteDisambiguationStatus === 'resolving' || akteDisambiguationStatus === 'llm_analyzing') && (
                          <p className="text-xs text-blue-600 dark:text-blue-400">
                            {akteDisambiguationStatus === 'resolving' ? 'Empfänger werden zugeordnet…' : 'KI prüft Inhalt auf eindeutigen Schülerbezug…'}
                          </p>
                        )}
                        {akteDisambiguationStatus === 'unambiguous' && saveToAkteStudentUnids.length > 0 && (
                          <p className="text-xs text-green-600 dark:text-green-400">Inhalt eindeutig zugeordnet.</p>
                        )}
                        {akteDisambiguationStatus === 'ambiguous' && (
                          <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 p-2">
                            <p className="text-xs font-medium text-amber-800 dark:text-amber-200">Der Inhalt ist nicht eindeutig.</p>
                            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">Bitte prüfen Sie die Liste unten und entfernen Sie ggf. nicht gemeinte Schüler, oder bestätigen Sie die Auswahl beim Senden.</p>
                            {akteDisambiguationReasoning && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 italic">{akteDisambiguationReasoning}</p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {saveToAkteMode === 'manual' && (
                      <div className="flex flex-wrap gap-2 items-center">
                        <input
                          type="text"
                          value={akteStudentSearch}
                          onChange={(e) => setAkteStudentSearch(e.target.value)}
                          placeholder="Schüler suchen (min. 2 Zeichen)..."
                          className="input-field min-h-[2.25rem] w-56 py-2 text-sm"
                        />
                        {akteSearchStudents.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {akteSearchStudents.slice(0, 8).map((s: any) => (
                              <button
                                key={s.sophomorixUnid}
                                type="button"
                                onClick={() => {
                                  if (!saveToAkteStudentUnids.includes(s.sophomorixUnid)) {
                                    setSaveToAkteStudentUnids([...saveToAkteStudentUnids, s.sophomorixUnid]);
                                    setSaveToAkteStudentInfo(prev => ({ ...prev, [s.sophomorixUnid]: { student_name: [s.Vorname, s.Name].filter(Boolean).join(' '), klasse: s.Klasse || '' } }));
                                  }
                                  setAkteStudentSearch('');
                                }}
                                className="px-2 py-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-xs hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                              >
                                {s.Vorname} {s.Name} ({s.Klasse})
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {saveToAkteStudentUnids.length > 0 && (
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Ablage in:</span>
                        {saveToAkteStudentUnids.map((unid) => (
                          <Chip
                            key={unid}
                            variant="warning"
                            onRemove={() => {
                              setSaveToAkteStudentUnids(saveToAkteStudentUnids.filter((u) => u !== unid));
                              setSaveToAkteStudentInfo((prev) => {
                                const n = { ...prev };
                                delete n[unid];
                                return n;
                              });
                            }}
                            removeLabel={`${saveToAkteStudentInfo[unid]?.student_name || unid} aus Ablage entfernen`}
                            title={unid}
                          >
                            {saveToAkteStudentInfo[unid]?.student_name || unid}
                            {saveToAkteStudentInfo[unid]?.klasse ? ` (${saveToAkteStudentInfo[unid].klasse})` : ''}
                          </Chip>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          
            {/* Betreff + Vorlage + Anhänge in einer Zeile */}
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Betreff</label>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="input-field min-h-[2.25rem] py-2 text-sm"
                  placeholder="Betreff eingeben"
                />
              </div>
              <div className="relative w-48 flex-shrink-0" ref={templateRef}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Vorlage</label>
                <button
                  type="button"
                  onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                  className="input-field text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                >
                  <span className="truncate text-gray-700 dark:text-gray-300">
                    {selectedTemplate ? templates.find((t: any) => t.id === selectedTemplate)?.name || 'Vorlage' : 'Optional'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${showTemplateDropdown ? 'transform rotate-180' : ''}`} />
                </button>
                {showTemplateDropdown && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {templates.length === 0 ? (
                      <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">Keine Vorlagen</div>
                    ) : (
                      <>
                        {templates.filter((t: any) => t.is_system_template).map((template: any) => (
                          <button key={template.id} type="button" onClick={() => { applyTemplate(template); setShowTemplateDropdown(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                            {template.name}
                          </button>
                        ))}
                        {templates.filter((t: any) => !t.is_system_template).map((template: any) => (
                          <button key={template.id} type="button" onClick={() => { applyTemplate(template); setShowTemplateDropdown(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                            {template.name}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" aria-hidden="true"> </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) {
                      addAttachmentFiles(e.target.files);
                    }
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-outline btn-sm flex items-center gap-2"
                  title="Datei hinzufügen (oder per Drag & Drop)"
                >
                  <Paperclip className="w-4 h-4" />
                  Anhänge
                </button>
              </div>
            </div>
            {attachments.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded text-sm text-gray-800 dark:text-gray-200">
                    <span className="text-gray-700 truncate max-w-[180px]">{attachment.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">({(attachment.size / 1024).toFixed(1)} KB)</span>
                    <button type="button" onClick={() => setAttachments(attachments.filter((_, i) => i !== index))} className="p-0.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">Dateien per Drag &amp; Drop ins Fenster ziehen oder über „Anhänge“ auswählen.</p>
            )}

            <div className="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-2">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Nachricht</label>
            {sendAsHtml ? (
              <>
                <div className="email-compose-toolbar">
                  <button type="button" onClick={() => handleHtmlFormat('bold')} className="email-compose-toolbar-btn" title="Fett"><Bold className="h-4 w-4" /></button>
                  <button type="button" onClick={() => handleHtmlFormat('italic')} className="email-compose-toolbar-btn" title="Kursiv"><Italic className="h-4 w-4" /></button>
                  <button type="button" onClick={() => handleHtmlFormat('underline')} className="email-compose-toolbar-btn" title="Unterstrichen"><Underline className="h-4 w-4" /></button>
                  <div className="w-px h-5 bg-gray-300 dark:bg-gray-600" />
                  <button type="button" onClick={() => handleHtmlFormat('insertUnorderedList')} className="email-compose-toolbar-btn" title="Aufzählung"><List className="h-4 w-4" /></button>
                  <div className="w-px h-5 bg-gray-300 dark:bg-gray-600" />
                  <button type="button" onClick={() => handleHtmlFormat('justifyLeft')} className="email-compose-toolbar-btn" title="Linksbündig"><AlignLeft className="h-4 w-4" /></button>
                  <button type="button" onClick={() => handleHtmlFormat('justifyCenter')} className="email-compose-toolbar-btn" title="Zentriert"><AlignCenter className="h-4 w-4" /></button>
                  <button type="button" onClick={() => handleHtmlFormat('justifyRight')} className="email-compose-toolbar-btn" title="Rechtsbündig"><AlignRight className="h-4 w-4" /></button>
                </div>
                <div
                  ref={htmlEditorRef}
                  contentEditable
                  onInput={() => htmlEditorRef.current && setBody(htmlEditorRef.current.innerHTML)}
                  className="email-compose-editor"
                  style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                  data-placeholder="Nachricht eingeben..."
                />
                <style>{`
                  [contenteditable][data-placeholder]:empty:before {
                    content: attr(data-placeholder);
                    color: #9ca3af;
                    pointer-events: none;
                  }
                  .dark [contenteditable][data-placeholder]:empty:before {
                    color: #6b7280;
                  }
                  .dark .email-compose-editor {
                    color: rgb(243 244 246);
                    background-color: rgb(17 24 39);
                  }
                `}</style>
              </>
            ) : (
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="email-compose-editor resize-y"
                placeholder="Nachricht eingeben"
              />
            )}
            </div>
      </div>
    </Modal>
    <EmailCredentialModal
      isOpen={showEmailCredentialModal}
      onClose={() => setShowEmailCredentialModal(false)}
      credential={null}
      requiredForSending={true}
      onSave={async (credentials) => {
        try {
          const resp = await api.post('/email/credentials', credentials);
          if (resp.data?.success) {
            toast.success('E-Mail-Konto erfolgreich hinzugefügt');
            setShowEmailCredentialModal(false);
            // Refresh fromEmail hint
            try {
              const res = await api.get('/email/credentials');
              const creds = res.data?.data || [];
              const primary = creds.find((c: any) => c.is_default) || creds[0];
              setFromEmail(primary?.email_address);
            } catch {}
          } else {
            toast.error(resp.data?.error || 'Fehler beim Speichern des E-Mail-Kontos');
          }
        } catch (err: any) {
          toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Fehler beim Speichern des E-Mail-Kontos');
        }
      }}
    />
    </>
  );
}


